import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Box, Button, Stack, Typography, FormControl, Select, MenuItem } from "@mui/material";

/**
 * Date range picker with quick presets for the Organization metrics dashboard.
 *
 * Props:
 *  - startDate: Date | null
 *  - endDate: Date | null
 *  - onChange: (update: [Date | null, Date | null]) => void
 */
const DateRangeSelector = ({ startDate, endDate, onChange }) => {
  const applyPreset = (preset) => {
    const now = new Date();
    let start;

    if (preset === "thisMonth") {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (preset === "last30") {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29);
    } else if (preset === "last90") {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 89);
    }

    onChange([start, now]);
  };

  // Build a reverse-chronological list of the last 24 months, e.g. "July, 2026".
  const monthOptions = React.useMemo(() => {
    const now = new Date();
    const options = [];
    for (let i = 0; i < 24; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth(); // 0-indexed
      const label = `${date.toLocaleString("en-US", { month: "long" })}, ${year}`;
      options.push({ value: `${year}-${month}`, label, year, month });
    }
    return options;
  }, []);

  // Determine which month option (if any) exactly matches the current range.
  const selectedMonthValue = React.useMemo(() => {
    if (!startDate || !endDate) return "";
    const now = new Date();
    const match = monthOptions.find((opt) => {
      const monthStart = new Date(opt.year, opt.month, 1);
      const isCurrent = opt.year === now.getFullYear() && opt.month === now.getMonth();
      const monthEnd = isCurrent
        ? now
        : new Date(opt.year, opt.month + 1, 0);
      return (
        startDate.getFullYear() === monthStart.getFullYear() &&
        startDate.getMonth() === monthStart.getMonth() &&
        startDate.getDate() === monthStart.getDate() &&
        endDate.getFullYear() === monthEnd.getFullYear() &&
        endDate.getMonth() === monthEnd.getMonth() &&
        endDate.getDate() === monthEnd.getDate()
      );
    });
    return match ? match.value : "";
  }, [startDate, endDate, monthOptions]);

  const handleMonthSelect = (value) => {
    const opt = monthOptions.find((o) => o.value === value);
    if (!opt) return;
    const now = new Date();
    const start = new Date(opt.year, opt.month, 1);
    const isCurrent = opt.year === now.getFullYear() && opt.month === now.getMonth();
    const end = isCurrent ? now : new Date(opt.year, opt.month + 1, 0);
    onChange([start, end]);
  };

  const presetButtonSx = {
    fontFamily: "Avenir Book, sans-serif",
    fontSize: 12,
    textTransform: "none",
    color: "#0AB2EE",
    borderColor: "#0AB2EE",
    padding: "3px 10px",
    minWidth: 0,
  };

  const fieldLabelSx = {
    fontFamily: "Avenir Book, sans-serif",
    fontSize: 12,
    color: "#666",
    fontWeight: 600,
    mb: 0.5,
  };

  const pickerBoxSx = {
    "& .react-datepicker-wrapper": { display: "inline-block" },
    "& .react-datepicker__input-container input": {
      fontFamily: "Avenir Book, sans-serif",
      fontSize: 14,
      padding: "8px 14px",
      border: "1px solid #ccc",
      borderRadius: "4px",
      backgroundColor: "#fff",
      minWidth: 150,
      outline: "none",
      cursor: "pointer",
    },
  };

  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-end" flexWrap="wrap">
      <Box>
        <Typography sx={fieldLabelSx}>Start Date</Typography>
        <Box sx={pickerBoxSx}>
          <DatePicker
            selectsStart
            selected={startDate}
            startDate={startDate}
            endDate={endDate}
            onChange={(date) => onChange([date, endDate])}
            maxDate={endDate || new Date()}
            dateFormat="MMM d, yyyy"
            placeholderText="Start date"
            withPortal
          />
        </Box>
      </Box>

      <Box>
        <Typography sx={fieldLabelSx}>End Date</Typography>
        <Box sx={pickerBoxSx}>
          <DatePicker
            selectsEnd
            selected={endDate}
            startDate={startDate}
            endDate={endDate}
            onChange={(date) => onChange([startDate, date])}
            minDate={startDate}
            maxDate={new Date()}
            dateFormat="MMM d, yyyy"
            placeholderText="End date"
            withPortal
          />
        </Box>
      </Box>

      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          size="small"
          sx={presetButtonSx}
          onClick={() => applyPreset("thisMonth")}
        >
          This Month
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={presetButtonSx}
          onClick={() => applyPreset("last30")}
        >
          Last 30 Days
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={presetButtonSx}
          onClick={() => applyPreset("last90")}
        >
          Last 90 Days
        </Button>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            displayEmpty
            value={selectedMonthValue}
            onChange={(e) => handleMonthSelect(e.target.value)}
            renderValue={(value) => {
              if (!value) return "Select month";
              const opt = monthOptions.find((o) => o.value === value);
              return opt ? opt.label : "Select month";
            }}
            sx={{
              fontFamily: "Avenir Book, sans-serif",
              fontSize: 14,
              backgroundColor: "#fff",
              borderRadius: 1,
              "& .MuiSelect-select": {
                padding: "8px 14px",
              },
            }}
          >
            {monthOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{ fontFamily: "Avenir Book, sans-serif", fontSize: 14 }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </Stack>
  );
};

export default DateRangeSelector;
